import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { ChevronDownIcon } from '@heroicons/react/16/solid';

import { ScheduledLessonOrdering } from '@/data/services/scheduledLessonsServices';
import { OrderingKey } from '@/models/Filter';
import { ScheduledLessonWithKlass } from '@/components/shared/pendencies/tables/types';
import {
  Button,
  SmartTableCellProps,
  TableColumn,
} from '@/components/ui/daisy';

import {
  ActionsCell,
  DateCell,
  KlassCell,
  LessonCell,
  UnitCell,
} from '@/components/shared/pendencies/tables/cells';
import { useMemo } from 'react';

export type AttendancePendencyColumnKey =
  | 'lesson'
  | 'unit'
  | 'klass'
  | 'date'
  | 'actions';

type UseAttendancePendencyColumnsParams = {
  ordering?: ScheduledLessonOrdering;
  onToggleOrdering: (field: OrderingKey<ScheduledLessonOrdering>) => void;
};

export default function useAttendancePendencyColumns({
  ordering,
  onToggleOrdering,
}: UseAttendancePendencyColumnsParams): TableColumn<
  ScheduledLessonWithKlass,
  AttendancePendencyColumnKey
>[] {
  const { t } = useTranslation('translation', { keyPrefix: 'pendencies' });

  return useMemo(
    () => [
      {
        key: 'lesson',
        header: t('columns.lesson'),
        Cell: LessonCell,
        headerClassName: 'w-[35%]',
        cellClassName: 'capitalize',
      },
      {
        key: 'unit',
        header: t('columns.unit'),
        Cell: ({ item }: SmartTableCellProps<ScheduledLessonWithKlass>) => (
          <UnitCell item={{ lessonBlock: item.lesson.lessonBlock ?? null }} />
        ),
        headerClassName: 'w-[15%]',
      },
      {
        key: 'klass',
        header: t('columns.klass'),
        Cell: KlassCell,
        headerClassName: 'w-[30%]',
      },
      {
        key: 'date',
        header: (
          <Button
            variant="ghost"
            size="sm"
            className="font-bold"
            onClick={() => onToggleOrdering('datetime')}
          >
            {t('columns.date')}
            <ChevronDownIcon
              className={twJoin(
                'w-4 transition-transform',
                ordering === 'datetime' && 'rotate-180',
              )}
            />
          </Button>
        ),
        Cell: DateCell,
        headerClassName: 'w-[20%]',
        cellClassName: 'whitespace-nowrap',
      },
      {
        key: 'actions',
        header: t('columns.actions'),
        Cell: ActionsCell,
        headerClassName: 'w-40 whitespace-nowrap',
        cellClassName: 'w-40 whitespace-nowrap',
      },
    ],
    [t, ordering, onToggleOrdering],
  );
}
